/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  DeleteAdminV1OrgProductsIdVariantsVariantIdPathParameters,
  GetAdminV1OrgProductsIdVariants200,
  GetAdminV1OrgProductsIdVariantsPathParameters,
  PatchAdminV1OrgProductsIdVariantsPathParameters,
  PatchAdminV1OrgProductsIdVariantsReorder200,
  PatchAdminV1OrgProductsIdVariantsReorderPathParameters,
  PatchAdminV1OrgProductsIdVariantsVariantIdBody,
  PatchAdminV1OrgProductsIdVariantsVariantIdPathParameters,
  PostAdminV1OrgProductsIdVariantsBody,
  PostAdminV1OrgProductsIdVariantsGenerate200,
  PostAdminV1OrgProductsIdVariantsGenerateBody,
  PostAdminV1OrgProductsIdVariantsGeneratePathParameters,
  PostAdminV1OrgProductsIdVariantsPathParameters,
  ReorderVariantsRequest,
  UpdateVariantsRequest,
  Variant,
  VariantList
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * A product has at most 2,000 variants, so the list is returned whole
 * @summary Variants of a product in position order
 */
export const getAdminV1OrgProductsIdVariants = (
    { id }: GetAdminV1OrgProductsIdVariantsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgProductsIdVariants200>>,) => {
      return customInstance<GetAdminV1OrgProductsIdVariants200>(
      {url: `/admin/v1/org/products/${id}/variants`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Add one variant for a specific combination of option values
 */
export const postAdminV1OrgProductsIdVariants = (
    { id }: PostAdminV1OrgProductsIdVariantsPathParameters,
    postAdminV1OrgProductsIdVariantsBody: BodyType<PostAdminV1OrgProductsIdVariantsBody>,
 options?: SecondParameter<typeof customInstance<Variant>>,) => {
      return customInstance<Variant>(
      {url: `/admin/v1/org/products/${id}/variants`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1OrgProductsIdVariantsBody
    },
      options);
    }
  /**
 * The variant table's save: each item names a variant of this product and the fields that change, with the same rules as updating one variant. All items are written in one transaction, so a refused item saves nothing; its field errors point at body.items.N. Answers every variant of the product in position order
 * @summary Update several variants in one save
 */
export const patchAdminV1OrgProductsIdVariants = (
    { id }: PatchAdminV1OrgProductsIdVariantsPathParameters,
    updateVariantsRequest: BodyType<UpdateVariantsRequest>,
 options?: SecondParameter<typeof customInstance<VariantList>>,) => {
      return customInstance<VariantList>(
      {url: `/admin/v1/org/products/${id}/variants`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: updateVariantsRequest
    },
      options);
    }
  /**
 * Each variant is priced from basePrice plus the modifiers of its option values (percentages first, then fixed amounts). SKUs come from the product type's SKU rule (D-161), {root}-{options} without one; a value already used gets the first free -N suffix. merge keeps existing variants; replace deletes them first and answers 409 RESOURCE_IN_USE (meta.variantIds, meta.usedBy) when any is published, stocked or on an order or cart line; archive keeps those as archived variants (their SKU stays with them, the new one is suffixed) and deletes the rest
 * @summary Generate every combination of the product's option values
 */
export const postAdminV1OrgProductsIdVariantsGenerate = (
    { id }: PostAdminV1OrgProductsIdVariantsGeneratePathParameters,
    postAdminV1OrgProductsIdVariantsGenerateBody: BodyType<PostAdminV1OrgProductsIdVariantsGenerateBody>,
 options?: SecondParameter<typeof customInstance<PostAdminV1OrgProductsIdVariantsGenerate200>>,) => {
      return customInstance<PostAdminV1OrgProductsIdVariantsGenerate200>(
      {url: `/admin/v1/org/products/${id}/variants/generate`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1OrgProductsIdVariantsGenerateBody
    },
      options);
    }
  /**
 * variantIds must list every variant of the product; positions follow the order sent
 * @summary Reorder the variants of a product
 */
export const patchAdminV1OrgProductsIdVariantsReorder = (
    { id }: PatchAdminV1OrgProductsIdVariantsReorderPathParameters,
    reorderVariantsRequest: BodyType<ReorderVariantsRequest>,
 options?: SecondParameter<typeof customInstance<PatchAdminV1OrgProductsIdVariantsReorder200>>,) => {
      return customInstance<PatchAdminV1OrgProductsIdVariantsReorder200>(
      {url: `/admin/v1/org/products/${id}/variants/reorder`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: reorderVariantsRequest
    },
      options);
    }
  /**
 * Option values never change on an existing variant; delete it and add another. A sku sent here is flagged manual and the previous value is retired, never to be reused; 409 SKU_RETIRED if the new value was used before
 * @summary Update a variant's price, identifiers, shipping and fulfillment fields
 */
export const patchAdminV1OrgProductsIdVariantsVariantId = (
    { id, variantId }: PatchAdminV1OrgProductsIdVariantsVariantIdPathParameters,
    patchAdminV1OrgProductsIdVariantsVariantIdBody: BodyType<PatchAdminV1OrgProductsIdVariantsVariantIdBody>,
 options?: SecondParameter<typeof customInstance<Variant>>,) => {
      return customInstance<Variant>(
      {url: `/admin/v1/org/products/${id}/variants/${variantId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1OrgProductsIdVariantsVariantIdBody
    },
      options);
    }
  /**
 * 409 RESOURCE_IN_USE with meta.usedBy otherwise; archive it instead (PATCH status archived)
 * @summary Delete a variant that no store, stock ledger, order or cart references
 */
export const deleteAdminV1OrgProductsIdVariantsVariantId = (
    { id, variantId }: DeleteAdminV1OrgProductsIdVariantsVariantIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/products/${id}/variants/${variantId}`, method: 'DELETE'
    },
      options);
    }
  export type GetAdminV1OrgProductsIdVariantsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgProductsIdVariants>>>
export type PostAdminV1OrgProductsIdVariantsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgProductsIdVariants>>>
export type PatchAdminV1OrgProductsIdVariantsResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgProductsIdVariants>>>
export type PostAdminV1OrgProductsIdVariantsGenerateResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgProductsIdVariantsGenerate>>>
export type PatchAdminV1OrgProductsIdVariantsReorderResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgProductsIdVariantsReorder>>>
export type PatchAdminV1OrgProductsIdVariantsVariantIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgProductsIdVariantsVariantId>>>
export type DeleteAdminV1OrgProductsIdVariantsVariantIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgProductsIdVariantsVariantId>>>
